<script lang="ts" module>
  import { formatNumber, formatShare } from '../../utils/format';

  export interface RankedRow {
    key: string;
    label: string;
    count: number;
    isCorrect: boolean;
    isGroup: boolean;
  }
</script>

<script lang="ts">
  import { checkmarkIcon, pencilLineIcon, plusIcon } from '@nrk/core-icons';
  import Icon from '../../lib/Icon.svelte';
  import { groupIcon } from '../../lib/icons/groupIcon';
  import { p } from '../../utils/router';

  interface Props {
    rows: RankedRow[];
    page: number;
    pageSize: number;
    guessTotal: number | undefined;
    participantId?: string;
    onCreateGroup?: (name: string, normalizedName: string) => Promise<void>;
    unmaskMode?: boolean;
    selectedGroups?: Set<string>;
    onToggleGroup?: (groupId: string) => void;
  }

  const {
    rows,
    page,
    pageSize,
    guessTotal,
    participantId,
    onCreateGroup,
    unmaskMode = false,
    selectedGroups = new Set(),
    onToggleGroup,
  }: Props = $props();

  const getGroupHref = (groupId: string): string => {
    if (participantId) {
      return p('/participants/:id/group/:groupId', {
        params: { id: participantId, groupId },
      });
    }
    return p('/groups/:id', { params: { id: groupId } });
  };

  let creatingKey = $state<string | null>(null);

  const getGroupId = (key: string): string | null =>
    key.startsWith('group:') ? key.slice('group:'.length) : null;

  const getNormalizedName = (key: string): string | null =>
    key.startsWith('name:') ? key.slice('name:'.length) : null;

  const handleCreateGroup = async (row: RankedRow) => {
    if (!onCreateGroup) return;
    const normalizedName = getNormalizedName(row.key);
    if (!normalizedName) return;

    creatingKey = row.key;
    try {
      await onCreateGroup(row.label, normalizedName);
    } finally {
      creatingKey = null;
    }
  };
</script>

<table class="ranking">
  <thead>
    <tr>
      <th scope="col" class="rank">#</th>
      <th scope="col" class="name">Gjett</th>
      <th scope="col" class="numeric">Antall</th>
      <th scope="col" class="numeric share">Andel</th>
      <th scope="col" class="group-col">Gruppe</th>
    </tr>
  </thead>
  <tbody>
    {#each rows as row, index (row.key)}
      {@const groupId = getGroupId(row.key)}
      <tr class:correct={row.isCorrect} class:is-group={row.isGroup}>
        <td class="rank">{(page - 1) * pageSize + index + 1}</td>
        <td class="name">
          <span class="label">
            {#if row.isGroup}
              <span class="group-icon" aria-label="Gruppe">
                <Icon svg={groupIcon} />
              </span>
            {/if}
            <span class="text" class:is-group={row.isGroup}>{row.label}</span>
            {#if row.isCorrect}
              <span class="correct">
                <Icon svg={checkmarkIcon} label="Riktig svar" />
              </span>
            {/if}
          </span>
        </td>
        <td class="numeric">{formatNumber(row.count)}</td>
        <td class="numeric share">{formatShare(row.count, guessTotal)}</td>
        <td class="group-col">
          {#if unmaskMode && groupId}
            <input
              type="checkbox"
              class="group-checkbox"
              checked={selectedGroups.has(groupId)}
              onchange={() => onToggleGroup?.(groupId)}
              aria-label="Marker som riktig"
            />
          {:else if groupId}
            <a
              href={getGroupHref(groupId)}
              class="group-link"
              title="Gå til gruppe"
            >
              <Icon svg={pencilLineIcon} label="Gå til gruppe" />
            </a>
          {:else if onCreateGroup && !unmaskMode}
            <button
              type="button"
              class="create-group-btn"
              title="Opprett gruppe"
              onclick={() => handleCreateGroup(row)}
              disabled={creatingKey === row.key}
            >
              <Icon svg={plusIcon} label="Opprett gruppe" />
            </button>
          {/if}
        </td>
      </tr>
    {/each}
  </tbody>
</table>

<style>
  .ranking {
    inline-size: 100%;
    border-collapse: collapse;

    & th {
      padding: var(--space-2) var(--space-3);
      border-block-end: var(--border-width) solid var(--border-strong);
      color: var(--text-muted);
      font-size: var(--text-sm);
      font-weight: var(--nrk-sans-weight-medium);
      text-align: start;
    }

    & td {
      padding: var(--space-3);
      border-block-end: var(--border-width) solid var(--border);
    }

    & tr.is-group {
      & td {
        background: var(--surface-raised);
      }
    }

    & tr.correct {
      display: table-row;
      color: var(--text);

      & td {
        background: light-dark(
          oklch(from var(--success) 97% calc(c * 0.2) h),
          oklch(from var(--success) 15% calc(c * 0.3) h)
        );

        &:first-child {
          border-inline-start: 3px solid var(--success);
        }
      }
    }

    & .rank {
      inline-size: var(--space-12);
      color: var(--text-muted);
      font-variant-numeric: tabular-nums;
      text-align: end;
    }

    & .numeric {
      inline-size: var(--space-16);
      font-variant-numeric: tabular-nums;
      text-align: end;
    }

    @media (width < 36rem) {
      max-inline-size: 100%;

      & .rank,
      & .numeric:not(.share) {
        display: none;
      }

      & .share {
        inline-size: 4.5rem;
      }
    }
  }

  .label {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
  }

  .text {
    @media (width < 36rem) {
      word-break: break-word;
    }

    &.is-group {
      font-weight: var(--nrk-sans-weight-bold);
    }
  }

  .group-icon {
    display: inline-flex;
    color: var(--accent);
  }

  .group-col {
    inline-size: var(--space-10);
    text-align: center;
  }

  .group-link,
  .create-group-btn {
    display: inline-flex;
    color: var(--text-muted);
    transition: color var(--duration-fast) var(--ease);
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;

    &:hover {
      color: var(--accent);
    }

    &:disabled {
      opacity: 0.5;
      cursor: wait;
    }
  }

  .correct {
    display: inline-flex;
    color: var(--success);
  }

  .group-checkbox {
    inline-size: 1.125rem;
    block-size: 1.125rem;
    accent-color: var(--accent);
    cursor: pointer;
  }
</style>
